import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { LightMode, DarkMode, Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { CATEGORIES } from '../config/categories';
import { STRIPE_PAYMENT_LINK } from '../config/stripe';
import { trackThemeToggle, trackDonationClick } from '../utils/analytics';

interface HeaderProps {
  isMobile: boolean;
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ isMobile, onMenuClick }) => {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const colors = {
    bg: isDark ? '#0f0f0f' : '#f5f5f5',
    headerBg: isDark ? '#161616' : '#fafafa',
    text: isDark ? '#e0e0e0' : '#1a1a1a',
    textSecondary: isDark ? '#999' : '#666',
    border: isDark ? '#1a1a1a' : '#f0f0f0',
    hover: isDark ? '#1c1c1c' : '#f8f8f8',
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      px: { xs: 2, md: 3 },
      py: 1.5,
      borderBottom: `1px solid ${colors.border}`,
      backgroundColor: colors.headerBg,
    }}>
      {/* Mobile Menu Button */}
      {isMobile && onMenuClick && (
        <IconButton
          onClick={onMenuClick}
          sx={{
            color: colors.text,
            '&:hover': {
              backgroundColor: colors.hover,
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Logo */}
      <Box
        component="img"
        src="/podcast.png"
        alt="Daily Stories"
        sx={{
          width: { xs: '28px', md: '36px' },
          height: 'auto',
          opacity: 0.9,
          cursor: 'pointer',
        }}
        onClick={() => navigate('/')}
      />

      {/* Categories - Desktop */}
      <Box sx={{ 
        display: { xs: 'none', lg: 'flex' },
        gap: 1,
        alignItems: 'center'
      }}>
        {CATEGORIES.map((category) => (
          <Box
            key={category.id}
            onClick={() => navigate(`/category/${category.id}`)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              }
            }}
          >
            <Typography sx={{ fontSize: '0.95rem' }}>
              {category.emoji}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: colors.text,
                fontWeight: 400,
                letterSpacing: '0.3px',
              }}
            >
              {category.name}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* About Link - Desktop */}
      <Box
        onClick={() => navigate('/about')}
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          px: 1.25,
          py: 0.5,
          borderRadius: '6px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          }
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: colors.text,
            fontWeight: 400,
            letterSpacing: '0.3px',
          }}
        >
          About
        </Typography>
      </Box>

      {/* Right Side Actions */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {/* Spotify - Desktop Only */}
        <IconButton
          onClick={() => window.open('https://open.spotify.com/show/6VVAtlHhnNrHUD67buhdBt', '_blank')}
          sx={{
            display: { xs: 'none', md: 'flex' },
            color: colors.textSecondary,
            '&:hover': {
              backgroundColor: colors.hover,
            },
          }}
          title="Listen on Spotify"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
          </svg>
        </IconButton>

        {/* Theme Toggle */}
        <IconButton
          onClick={() => {
            toggleTheme();
            trackThemeToggle(isDark ? 'light' : 'dark');
          }}
          sx={{
            color: colors.textSecondary,
            '&:hover': {
              backgroundColor: colors.hover,
            },
          }}
        >
          {isDark ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
        </IconButton>

        {/* Martini Button */}
        <IconButton
          onClick={() => {
            trackDonationClick();
            window.open(STRIPE_PAYMENT_LINK, '_blank');
          }}
          sx={{
            backgroundColor: colors.text,
            color: isDark ? '#161616' : '#fafafa',
            px: { xs: 1.5, sm: 2 },
            py: 0.75,
            borderRadius: '16px',
            '&:hover': {
              backgroundColor: colors.textSecondary,
            },
          }}
        >
          <Typography sx={{ fontSize: '0.95rem', mr: { xs: 0, sm: 0.5 } }}>🍸</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, display: { xs: 'none', sm: 'block' } }}>
            Martini
          </Typography>
        </IconButton>
      </Box>
    </Box>
  );
};

export default Header;
